import { useEffect } from 'react'
import { trackEvent } from '@/lib/telemetry'

// Turn a click on any element carrying `data-track-event` into a telemetry UI
// event. Exported (not just inlined in the hook) so the privacy invariant below
// is directly unit-testable with a synthetic DOM event.
//
// Privacy invariant (test-enforced): the target is read ONLY from
// `data-track-target` (or the element id as a fallback), NEVER from href or text
// content - so a mailto link's email, a bot-token label, or any raw customer id
// never reaches telemetry. Whoever adds `data-track-target` to an element must
// keep that value free of secrets/PII.
export function handleTrackingClick(e: MouseEvent): void {
  // e.target can be a text node or SVG element; normalise to an Element
  // before calling closest.
  const node = e.target
  const start = node instanceof Element ? node : (node as Node | null)?.parentElement ?? null
  const el = start?.closest?.('[data-track-event]')
  if (!el) return

  const type = el.getAttribute('data-track-event')
  if (!type) return

  const target = el.getAttribute('data-track-target') || el.id || undefined
  trackEvent(type, target)
}

// One document-level, capture-phase click listener. Capture phase means it still
// fires if an inner handler calls stopPropagation.
export function useEventTracking() {
  useEffect(() => {
    document.addEventListener('click', handleTrackingClick, true)
    return () => document.removeEventListener('click', handleTrackingClick, true)
  }, [])
}
